"use client";

import React from "react";
import { Plus, CircleCheck, Settings2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const hotspots = [{
  id: "dial",
  top: "45%",
  left: "50%",
  title: "Dial Branding",
  description: "Precision printing or metal applique techniques to showcase your core brand identity with various finishes.",
  icon: "CircleCheck"
}, {
  id: "crown",
  top: "50%",
  left: "72%",
  title: "Crown Engraving",
  description: "Laser or deep-pressed engraving on the crown to enhance tactile feel and brand premium.",
  icon: "Settings2"
}, {
  id: "caseback",
  top: "25%",
  left: "65%",
  title: "Custom Caseback",
  description: "Deep engraving of serial numbers, technical specs, and your secondary logo or brand story.",
  icon: "CircleCheck"
}, {
  id: "buckle",
  top: "80%",
  left: "40%",
  title: "Buckle Detailing",
  description: "Subtle yet impactful branding on the buckle hardware ensures 360-degree brand visibility.",
  icon: "Settings2"
}];
export default function CustomizationOptions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: galleryRef,
    isVisible: isGalleryVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section id="customization-options" className="py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-6 md:px-12">
        
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 font-sans">What You Can Customize</h2>
          <p className="text-lg text-muted-foreground leading-relaxed">Even with small batch production, you can still build your own brand identity.</p>
        </div>

        {}
        <div ref={galleryRef} className={`relative max-w-5xl mx-auto transition-all duration-1000 delay-200 ease-out ${isGalleryVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
          {}
          <div className="relative aspect-square md:aspect-[16/9] bg-secondary/10 rounded-lg overflow-hidden border border-border shadow-2xl flex items-center justify-center">
            {}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-secondary-200)_0%,transparent_70%)] opacity-30 pointer-events-none" />
            
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590442/img/watch_customization_base.png" alt="Watch customization details showcase" className="w-full h-full object-contain p-8 md:p-16 relative z-10 drop-shadow-2xl" />

            {}
            {hotspots.map((hotspot, i) => <div key={hotspot.id} data-index={i} className="absolute z-20" style={{
            top: hotspot.top,
            left: hotspot.left,
            transform: 'translate(-50%, -50%)'
          }}>
                <Popover>
                  <PopoverTrigger asChild>
                    <button className="group relative flex items-center justify-center size-8 md:size-10 rounded-md bg-accent text-accent-foreground shadow-lg hover:scale-110 transition-transform duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background" aria-label={`View details for ${hotspot.title}`}>
                      {}
                      <span className="absolute inset-0 rounded-md bg-accent/40 animate-ping opacity-75 group-hover:hidden" />
                      <Plus className="size-4 md:size-5 transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="top" align="center" sideOffset={16} className="w-72 p-6 rounded-lg shadow-xl border-border bg-card/95 backdrop-blur-sm">
                    <div className="flex flex-col gap-4">
                      <div className="flex items-center gap-3 border-b border-border pb-4">
                        <div className="flex items-center justify-center size-8 rounded-md bg-secondary text-secondary-foreground">
                          {hotspot.icon === 'CircleCheck' ? <CircleCheck className="size-4" strokeWidth={1.5} /> : <Settings2 className="size-4" strokeWidth={1.5} />}
                        </div>
                        <h4 className="font-semibold text-base tracking-tight text-card-foreground font-sans">
                          {hotspot.title}
                        </h4>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {hotspot.description}
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>)}
          </div>

          {}
          <div className="mt-8 text-center md:hidden">
            <p className="text-xs font-mono text-muted-foreground flex items-center justify-center gap-2 uppercase tracking-wider">
              <span className="flex items-center justify-center size-5 rounded-md bg-accent text-accent-foreground">
                <Plus className="size-3" strokeWidth={1.5} />
              </span>
              Click hotspots to explore customization details
            </p>
          </div>
        </div>

      </div>
    </section>;
}